import { useState } from 'react';

import type { Meta, StoryFn } from '@storybook/react';
import type { SearchFieldProps } from './SearchField';

import { SearchField } from './SearchField';
import { MOCK_DATA } from './mockData';

const codeString = `
// Has all HTMLInputElement props available except "className" and "style" 
<SearchField 
  id={string} 
  queryLength?={number}
  placeholder?={string} 
  isLoading?={boolean} 
  isCompact?={boolean} 
  onChange={function}
  children={ReactNode}
  resultCountRow?={ReactNode}
>
  <SearchField.ResultOption 
    onSelect={function} 
    onEscape={function} 
    value={string|number} 
    key={string|number} 
    ariaLabel={string}>
   {loop custom options here}
  </SearchField.ResultOption>
</SearchField>`;

const MIN_QUERY_LENGTH = 4;
const MAX_RESULTS = 12;
const QUERY_SPEED = 1000; // ms

enum ProgrammeType {
  DEGREE = 'degree-programme',
  CERTIFICATE = 'certificate-programme',
  ADMINISTRATIVE = 'administrative-programme',
}

interface SearchResult {
  __typename: string;
  id: string;
  code: string;
  name: {
    EN: string;
    NL: string;
  };
  programmeType: string;
  programmeCode?: string;
}

// eslint-disable-next-line import/no-default-export
export default {
  title: 'Molecules/SearchField',
  component: SearchField,
  args: {},
  argTypes: {
    onSelect: {
      action: 'selected url',
      table: {
        disable: true,
      },
    },
    onChange: {
      table: {
        disable: true,
      },
    },
    children: {
      table: {
        disable: true,
      },
    },
    id: {
      table: {
        disable: true,
      },
    },
    resultCountRow: {
      table: {
        disable: true,
      },
    },
    isCompact: {
      control: 'boolean',
      default: false,
    },
  },
  parameters: {
    inspectComponent: SearchField,
    codeString: codeString,
  },
} as Meta<SearchFieldProps & { onSelect: (value: string | number) => void }>;

const Template: StoryFn<SearchFieldProps & { onSelect: (value: string | number) => void }> = (args) => {
  const [loading, setLoading] = useState(false);
  const [searchResults, setSearchResults] = useState<SearchResult[] | null>();
  const academicYear = '2026';
  const currentLanguage = 'NL';

  const createUrl = (
    academicYear: string,
    programmeType: keyof typeof ProgrammeType,
    programmeCode: string,
    code: string
  ) => `education-preparation/${ProgrammeType[programmeType]}/${academicYear}/${programmeCode}/${code}`;

  const searchInMockData = (query: string) => {
    if (query.length < MIN_QUERY_LENGTH) {
      setSearchResults(null);
      return;
    }
    setLoading(true);

    const lowerCaseQuery = query.toLowerCase();

    const mockResults = MOCK_DATA.filter(
      (item) => item.name.NL.toLowerCase().includes(lowerCaseQuery) || item.code.toLowerCase().includes(lowerCaseQuery)
    );

    setTimeout(() => {
      setSearchResults(mockResults);
      setLoading(false);
    }, QUERY_SPEED);
  };

  const getResultMessage = (results: SearchResult[]) => {
    if (results.length > MAX_RESULTS) {
      return `${results.length} resultaten gevonden, alleen de ${MAX_RESULTS} beste resultaten worden weergegeven.`;
    } else if (results.length > 0 && results.length <= MAX_RESULTS) {
      return `${results.length} resultaten gevonden.`;
    } else if (loading) {
      return 'Zoeken...';
    } else {
      return 'Geen resultaten gevonden.';
    }
  };

  const resultBar = (results: SearchResult[]) =>
    results ? (
      <div key={'result-count'} style={{ padding: '0.5rem 1rem', fontWeight: '600' }}>
        {getResultMessage(results)}
      </div>
    ) : null;

  const handleSelect = (value: string | number) => {
    setSearchResults(null);
    args.onSelect(value);
  };

  return (
    <div style={{ width: '20rem', display: 'flex', justifyContent: 'flex-end' }}>
      <SearchField
        placeholder="Zoek..."
        id="search-field-1"
        isLoading={loading}
        isCompact={args.isCompact}
        onChange={(event) => searchInMockData(event)}
        resultCountRow={resultBar(searchResults || [])}
      >
        {searchResults?.map((result) => (
          <SearchField.ResultOption
            key={result.id}
            ariaLabel={`${result.code}, ${result.name[currentLanguage]}`}
            value={createUrl(
              academicYear,
              result.programmeType as keyof typeof ProgrammeType,
              result.programmeCode || '',
              result.code
            )}
            onSelect={handleSelect}
            onEscape={() => setSearchResults(null)}
          >
            <div style={{ display: 'flex', gap: '1rem' }}>
              <span>{result.code}</span>
              <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {result.name[currentLanguage]}
              </span>
            </div>
          </SearchField.ResultOption>
        ))}
      </SearchField>
    </div>
  );
};

export const InputExample = Template.bind({});
